import {t} from 'i18next';
import React, {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {usePlayerLabels} from '../../../../hooks/usePlayerLabels';
import {useSlideSelectable} from '../../../../hooks/useSlideSelectable';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {IElementQuestionnaire} from '../../../../incase-domain/elements/base/questionElementBase';
import {IElementQuestionMatchPair} from '../../../../incase-domain/elements/question/questionMatchPair';
import {
    LikertScaleInteractions,
    QuestionElementInteractions,
    QuestionMatchPairInteractions,
    YesNoElementInteractions,
} from '../../../../incase-domain/proptypes/runfile';
import {scrollLeftAction, scrollRightAction} from '../../../../store/actions/presentation.actions';
import {currentSlideIndexSelector} from '../../../../store/selectors/presentation.selectors';
import {runFileElementInteractionsSelector} from '../../../../store/selectors/runfile.selectors';
import useQuestionsTry from '../../../element/types/question/useQuestionsTry';
import {IElementLikertScale} from '../../../../incase-domain/elements/question/likertScale';
import Button from '../../../../incase-domain/components/button/Button';

interface MobileFooterProps {
    currentQuestion: IElementQuestionnaire | undefined;
}
function MobileFooter(props: MobileFooterProps): JSX.Element {
    const dispatch = useDispatch();
    const {currentQuestion} = props;
    const getLabel = usePlayerLabels();

    const currentSlideIndex = useSelector(currentSlideIndexSelector);

    const elementInteractionsBySlide = useSelector(runFileElementInteractionsSelector);

    const {isSelectable, blockedTooltipKey} = useSlideSelectable(currentSlideIndex + 1);

    const handleTry = useQuestionsTry(currentQuestion);

    const currentQuestionInteraction = useMemo(() => {
        if (!currentQuestion) return undefined;

        const slideElementInteractions = elementInteractionsBySlide ? elementInteractionsBySlide[currentQuestion.slideId] : {};
        let elementInteractions: QuestionElementInteractions | YesNoElementInteractions | QuestionMatchPairInteractions = {} as
            | QuestionElementInteractions
            | YesNoElementInteractions
            | QuestionMatchPairInteractions;
        if (slideElementInteractions && slideElementInteractions[currentQuestion.id]) {
            elementInteractions = slideElementInteractions[currentQuestion.id] as
                | QuestionElementInteractions
                | YesNoElementInteractions
                | QuestionMatchPairInteractions;
        }

        return elementInteractions;
    }, [currentQuestion, elementInteractionsBySlide]);

    const handleRightClick = (): void => {
        if (isSelectable) {
            dispatch(scrollRightAction());
        } else {
            toast.error(t(blockedTooltipKey), {toastId: blockedTooltipKey});
        }
    };

    function getConfirmButton(): JSX.Element | null {
        if (!currentQuestion) return null;

        if (currentQuestion.type === ELEMENT.YES_NO_QUESTION) {
            return (
                <>
                    {(currentQuestionInteraction as YesNoElementInteractions)?.answer !== undefined ? (
                        <Button
                            fullWidth
                            size="lg"
                            color="info"
                            type="button"
                            onClick={() => handleTry((currentQuestionInteraction as YesNoElementInteractions)?.answer)}
                            disabled={!(currentQuestionInteraction as YesNoElementInteractions)?.answer === undefined}
                        >
                            <span>{getLabel('elements-question-try') || t('common.confirm')}</span>
                        </Button>
                    ) : (
                        <div className="u-p-2 u-text-overflow">{t('question.selectAnAnswer')}</div>
                    )}
                </>
            );
        }

        if (currentQuestion.type === ELEMENT.MATCH_PAIR_QUESTION) {
            return (
                <>
                    {(currentQuestionInteraction as QuestionMatchPairInteractions)?.submittedPairs?.length ===
                        (currentQuestion as IElementQuestionMatchPair).pairs?.length &&
                    !(currentQuestionInteraction as QuestionMatchPairInteractions)?.submittedPairs?.some(
                        (p) => !p.leftSelectedElement || !p.rightSelectedElement
                    ) ? (
                        <Button
                            onClick={() => handleTry()}
                            disabled={!(currentQuestionInteraction as QuestionMatchPairInteractions)?.submittedPairs?.length}
                            type="button"
                            fullWidth
                            size="lg"
                            color="info"
                        >
                            <span>{getLabel('elements-question-try') || t('common.confirm')}</span>
                        </Button>
                    ) : (
                        <div className="u-p-2 u-text-overflow">{t('question.selectAnAnswer')}</div>
                    )}
                </>
            );
        }

        if (
            [
                ELEMENT.QUESTION,
                ELEMENT.MULTI_SELECT,
                ELEMENT.SEQUENCE_QUESTION,
                ELEMENT.PHOTO_ANSWER_QUESTION,
                ELEMENT.IMAGE_MARKUP,
                ELEMENT.YES_NO_QUESTION_V2,
            ].includes(currentQuestion.type)
        ) {
            return (
                <>
                    {(currentQuestionInteraction as QuestionElementInteractions)?.markedAnswers?.length ||
                    (currentQuestionInteraction as QuestionElementInteractions)?.markedAnswersIds?.length ? (
                        <Button
                            fullWidth
                            size="lg"
                            color="info"
                            type="button"
                            onClick={() => handleTry()}
                            disabled={
                                !(currentQuestionInteraction as QuestionElementInteractions)?.markedAnswers?.length &&
                                !(currentQuestionInteraction as QuestionElementInteractions)?.markedAnswersIds?.length
                            }
                        >
                            <span>{getLabel('elements-question-try') || t('common.confirm')}</span>
                        </Button>
                    ) : (
                        <div className="u-p-2 u-text-overflow">{t('question.selectAnAnswer')}</div>
                    )}
                </>
            );
        }

        if ([ELEMENT.LIKERT_SCALE].includes(currentQuestion.type)) {
            return (
                <>
                    {!(
                        ((currentQuestionInteraction as LikertScaleInteractions).likertScaleAnswerClicked?.length || 0) <
                        (currentQuestion as IElementLikertScale).questions?.length
                    ) ? (
                        <Button
                            size="lg"
                            fullWidth
                            color="info"
                            type="button"
                            onClick={() => handleTry()}
                            disabled={
                                ((currentQuestionInteraction as LikertScaleInteractions).likertScaleAnswerClicked?.length || 0) <
                                (currentQuestion as IElementLikertScale).questions?.length
                            }
                        >
                            <span>{getLabel('elements-question-try') || t('common.confirm')}</span>
                        </Button>
                    ) : (
                        <div className="u-p-2 u-text-overflow">{t('question.selectAnAnswer')}</div>
                    )}
                </>
            );
        }

        return null;
    }

    return (
        <>
            <div className="p-player__mobile_footer">
                <Button
                    disabled={currentSlideIndex === 0}
                    onClick={() => dispatch(scrollLeftAction())}
                    leading="solar:alt-arrow-left-linear"
                    size="xl"
                    variant="ghost"
                />
                <div className="p-player__answerContainer">
                    {!currentQuestion ? (
                        <Button size="lg" disabled={!isSelectable} onClick={handleRightClick} fullWidth>
                            <div>{t('footer.nextSlide')}</div>
                        </Button>
                    ) : (
                        getConfirmButton()
                    )}
                </div>

                <Button
                    disabled={!isSelectable}
                    onClick={handleRightClick}
                    leading="solar:alt-arrow-right-linear"
                    size="xl"
                    variant="ghost"
                />
            </div>
        </>
    );
}

export default MobileFooter;
